import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { eventTypesAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface EventTypeStats {
  event_type: any;
  period: { days: number; start_date: string };
  summary: {
    total_count: number;
    unique_visitors: number;
    unique_fingerprints: number;
    unique_ips: number;
    total_sessions: number;
    total_value: number | null;
    avg_value: number | null;
    min_value: number | null;
    max_value: number | null;
  };
  daily_trend: Array<{ date: string; count: number; unique_users: number; value: number }>;
  device_breakdown: Array<{ device_type: string; count: number; unique_users: number }>;
  browser_breakdown: Array<{ browser: string; count: number }>;
  os_breakdown: Array<{ os: string; count: number }>;
  country_breakdown: Array<{ country: string; count: number; unique_users: number; total_value: number }>;
  user_type_breakdown: Array<{ user_type: string; count: number; unique_users: number; total_value: number }>;
  custom_data_keys: Array<{ key: string; count: number }>;
}

interface EventLog {
  id: number;
  visitor_id: string;
  fingerprint: string;
  session_id: string;
  event_name: string;
  event_category: string;
  event_action: string;
  event_label: string;
  event_value: number;
  currency: string;
  page_url: string;
  ip_address: string;
  device_type: string;
  browser: string;
  os: string;
  country: string;
  city: string;
  user_type: string;
  custom_data: any;
  created_at: string;
  asset_name: string;
}

interface CodeSnippets {
  event_type: any;
  asset: { name: string; tracking_id: string; api_key: string };
  snippets: {
    [key: string]: {
      title: string;
      description: string;
      code: string;
    };
  };
}

const EventPointDetailPage: React.FC = () => {
  useDocumentTitle('Event Details');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentAsset } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<EventTypeStats | null>(null);
  const [events, setEvents] = useState<EventLog[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsPagination, setEventsPagination] = useState({ total: 0, page: 1, limit: 50, pages: 0 });
  const [snippets, setSnippets] = useState<CodeSnippets | null>(null);
  const [snippetsLoading, setSnippetsLoading] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchStats();
    }
  }, [id, period, currentAsset]);

  useEffect(() => {
    if (activeTab === 'events' && events.length === 0) {
      fetchEvents();
    }
    if (activeTab === 'integration' && !snippets) {
      fetchSnippets();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await eventTypesAPI.getStats(parseInt(id), period, currentAsset?.id);
      setStats(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch event type stats:', error);
      toast.error('Failed to load event statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async (page: number = 1) => {
    if (!id) return;
    setEventsLoading(true);
    try {
      const response = await eventTypesAPI.getEvents(parseInt(id), page, 50, currentAsset?.id);
      setEvents(response.data.data.events);
      setEventsPagination(response.data.data.pagination);
    } catch (error: any) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchSnippets = async () => {
    if (!id) return;
    setSnippetsLoading(true);
    try {
      const response = await eventTypesAPI.getCodeSnippets(parseInt(id));
      setSnippets(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch code snippets:', error);
      toast.error('Failed to load code snippets');
    } finally {
      setSnippetsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleteLoading(true);
    try {
      await eventTypesAPI.delete(parseInt(id));
      toast.success('Event point deleted');
      navigate('/events');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete event point');
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const copyToClipboard = (code: string, key: string) => {
    navigator.clipboard.writeText(code);
    setCopiedSnippet(key);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedSnippet(null), 2000);
  };

  const formatNumber = (num: number) => new Intl.NumberFormat().format(num);
  const formatCurrency = (num: number) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2
  }).format(num);
  const formatDate = (date: string) => new Date(date).toLocaleString();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <p className="text-muted">Loading event details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!stats) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">
          <i className="bi bi-exclamation-circle fs-1 text-muted"></i>
          <h4 className="mt-3">Event Point Not Found</h4>
          <Link to="/events" className="btn btn-primary mt-3">Back to Register Events</Link>
        </div>
      </DashboardLayout>
    );
  }

  const eventType = stats.event_type;

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-2">
                    <li className="breadcrumb-item"><Link to="/events">Register Events</Link></li>
                    <li className="breadcrumb-item active">{eventType.display_name}</li>
                  </ol>
                </nav>
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '48px', height: '48px',
                      backgroundColor: eventType.color + '20', color: eventType.color
                    }}
                  >
                    <i className={`bi bi-${eventType.icon || 'tag'} fs-4`}></i>
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h2 className="h3 mb-1">{eventType.display_name}</h2>
                      {eventType.is_goal && (
                        <span className="badge mb-1" style={{ background: '#fff3cd', color: '#92400e' }}>
                          <i className="bi bi-trophy-fill me-1" style={{ color: '#f59e0b' }}></i>Goal / Conversion
                        </span>
                      )}
                    </div>
                    <code className="text-muted">{eventType.name}</code>
                    {eventType.description && (
                      <p className="text-muted mb-0 mt-1">{eventType.description}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <i className="bi bi-trash me-1"></i>Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <i className="bi bi-graph-up me-2"></i>Overview
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'events' ? 'active' : ''}`}
              onClick={() => setActiveTab('events')}
            >
              <i className="bi bi-list-ul me-2"></i>Events Log
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'integration' ? 'active' : ''}`}
              onClick={() => setActiveTab('integration')}
            >
              <i className="bi bi-code-slash me-2"></i>Integration
            </button>
          </li>
        </ul>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Period Selector */}
            <div className="d-flex justify-content-end mb-4">
              <div className="btn-group">
                {['7d', '30d', '90d'].map((p) => (
                  <button
                    key={p}
                    className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod(p)}
                  >
                    {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row g-4 mb-4">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <p className="text-muted small mb-1">Total Events</p>
                    <h3 className="h4 mb-0">{formatNumber(stats.summary.total_count)}</h3>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <p className="text-muted small mb-1">Unique Users</p>
                    <h3 className="h4 mb-0">{formatNumber(stats.summary.unique_visitors)}</h3>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <p className="text-muted small mb-1">Sessions</p>
                    <h3 className="h4 mb-0">{formatNumber(stats.summary.total_sessions)}</h3>
                  </div>
                </div>
              </div>
              {eventType.has_value && (
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <p className="text-muted small mb-1">Total {eventType.value_label || 'Value'}</p>
                      <h3 className="h4 mb-0 text-success">
                        {eventType.value_type === 'currency'
                          ? formatCurrency(stats.summary.total_value || 0)
                          : formatNumber(stats.summary.total_value || 0)}
                      </h3>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Daily Trend */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">Daily Trend</h6>
                  </div>
                  <div className="card-body">
                    {stats.daily_trend.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th className="text-end">Events</th>
                              <th className="text-end">Users</th>
                              {eventType.has_value && <th className="text-end">Value</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {stats.daily_trend.map((day, i) => (
                              <tr key={i}>
                                <td>{new Date(day.date).toLocaleDateString()}</td>
                                <td className="text-end">{formatNumber(day.count)}</td>
                                <td className="text-end">{formatNumber(day.unique_users)}</td>
                                {eventType.has_value && (
                                  <td className="text-end text-success">{formatCurrency(day.value)}</td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted text-center py-3 mb-0">No data for this period</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Breakdowns */}
            <div className="row g-4">
              {/* Device Breakdown */}
              <div className="col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h6 className="mb-0"><i className="bi bi-phone me-2"></i>Devices</h6>
                  </div>
                  <div className="card-body">
                    {stats.device_breakdown.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {stats.device_breakdown.map((d, i) => (
                          <li key={i} className="d-flex justify-content-between py-2 border-bottom">
                            <span>{d.device_type}</span>
                            <span className="fw-medium">{formatNumber(d.count)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted text-center mb-0">No device data</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Browser Breakdown */}
              <div className="col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h6 className="mb-0"><i className="bi bi-browser-chrome me-2"></i>Browsers</h6>
                  </div>
                  <div className="card-body">
                    {stats.browser_breakdown.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {stats.browser_breakdown.slice(0, 5).map((b, i) => (
                          <li key={i} className="d-flex justify-content-between py-2 border-bottom">
                            <span>{b.browser}</span>
                            <span className="fw-medium">{formatNumber(b.count)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted text-center mb-0">No browser data</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Country Breakdown */}
              <div className="col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h6 className="mb-0"><i className="bi bi-globe me-2"></i>Countries</h6>
                  </div>
                  <div className="card-body">
                    {stats.country_breakdown.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {stats.country_breakdown.slice(0, 5).map((c, i) => (
                          <li key={i} className="d-flex justify-content-between py-2 border-bottom">
                            <span>{c.country}</span>
                            <div>
                              <span className="fw-medium">{formatNumber(c.count)}</span>
                              {eventType.has_value && c.total_value > 0 && (
                                <small className="text-success ms-2">{formatCurrency(c.total_value)}</small>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted text-center mb-0">No location data</p>
                    )}
                  </div>
                </div>
              </div>

              {/* User Type Breakdown */}
              <div className="col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h6 className="mb-0"><i className="bi bi-person me-2"></i>User Types</h6>
                  </div>
                  <div className="card-body">
                    {stats.user_type_breakdown.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {stats.user_type_breakdown.map((u, i) => (
                          <li key={i} className="d-flex justify-content-between py-2 border-bottom">
                            <span className="text-capitalize">{u.user_type}</span>
                            <div>
                              <span className="fw-medium">{formatNumber(u.count)}</span>
                              {eventType.has_value && u.total_value > 0 && (
                                <small className="text-success ms-2">{formatCurrency(u.total_value)}</small>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted text-center mb-0">No user type data</p>
                    )}
                  </div>
                </div>
              </div>

              {/* OS Breakdown */}
              <div className="col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h6 className="mb-0"><i className="bi bi-windows me-2"></i>Operating Systems</h6>
                  </div>
                  <div className="card-body">
                    {stats.os_breakdown.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {stats.os_breakdown.slice(0, 5).map((o, i) => (
                          <li key={i} className="d-flex justify-content-between py-2 border-bottom">
                            <span>{o.os}</span>
                            <span className="fw-medium">{formatNumber(o.count)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted text-center mb-0">No OS data</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Custom Data Keys */}
              {stats.custom_data_keys.length > 0 && (
                <div className="col-md-6 col-lg-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h6 className="mb-0"><i className="bi bi-braces me-2"></i>Custom Attributes</h6>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled mb-0">
                        {stats.custom_data_keys.slice(0, 5).map((k, i) => (
                          <li key={i} className="d-flex justify-content-between py-2 border-bottom">
                            <code>{k.key}</code>
                            <span className="fw-medium">{formatNumber(k.count)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Recent Events</h6>
              <span className="badge bg-secondary">{formatNumber(eventsPagination.total)} total</span>
            </div>
            <div className="card-body p-0">
              {eventsLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : events.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Time</th>
                        <th>Visitor</th>
                        <th>Device</th>
                        <th>Location</th>
                        <th>User Type</th>
                        {eventType.has_value && <th className="text-end">Value</th>}
                        <th>Page</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.id}>
                          <td>
                            <small>{formatDate(event.created_at)}</small>
                          </td>
                          <td>
                            <code className="small">{event.visitor_id || event.fingerprint || '-'}</code>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark">
                              {event.device_type || 'Unknown'}
                            </span>
                            <br />
                            <small className="text-muted">{event.browser} / {event.os}</small>
                          </td>
                          <td>
                            {event.country ? (
                              <>
                                {event.country}
                                {event.city && <small className="text-muted d-block">{event.city}</small>}
                              </>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${event.user_type === 'anonymous' ? 'bg-secondary' : 'bg-info'}`}>
                              {event.user_type || 'anonymous'}
                            </span>
                          </td>
                          {eventType.has_value && (
                            <td className="text-end text-success fw-medium">
                              {event.event_value ? formatCurrency(event.event_value) : '-'}
                            </td>
                          )}
                          <td>
                            {event.page_url ? (
                              <a
                                href={event.page_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-truncate d-block"
                                style={{ maxWidth: '200px' }}
                              >
                                {new URL(event.page_url).pathname}
                              </a>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-inbox fs-1 text-muted"></i>
                  <p className="text-muted mt-2">No events recorded yet</p>
                </div>
              )}
            </div>
            {eventsPagination.pages > 1 && (
              <div className="card-footer bg-white">
                <nav>
                  <ul className="pagination pagination-sm mb-0 justify-content-center">
                    {Array.from({ length: Math.min(eventsPagination.pages, 10) }, (_, i) => i + 1).map((page) => (
                      <li
                        key={page}
                        className={`page-item ${eventsPagination.page === page ? 'active' : ''}`}
                      >
                        <button className="page-link" onClick={() => fetchEvents(page)}>
                          {page}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            )}
          </div>
        )}

        {/* Integration Tab */}
        {activeTab === 'integration' && (
          <>
            {snippetsLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary"></div>
                <p className="text-muted mt-2">Loading code snippets...</p>
              </div>
            ) : snippets ? (
              <div className="row g-4">
                <div className="col-12">
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>API Key:</strong> <code>{snippets.asset.api_key}</code>
                    <button
                      className="btn btn-sm btn-outline-secondary ms-2"
                      onClick={() => copyToClipboard(snippets.asset.api_key, 'api_key')}
                    >
                      {copiedSnippet === 'api_key' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {Object.entries(snippets.snippets).map(([key, snippet]) => (
                  <div key={key} className="col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-white d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">{snippet.title}</h6>
                          <small className="text-muted">{snippet.description}</small>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => copyToClipboard(snippet.code, key)}
                        >
                          {copiedSnippet === key ? (
                            <><i className="bi bi-check me-1"></i>Copied!</>
                          ) : (
                            <><i className="bi bi-clipboard me-1"></i>Copy</>
                          )}
                        </button>
                      </div>
                      <div className="card-body bg-dark p-0">
                        <pre className="mb-0 p-3" style={{ maxHeight: '400px', overflow: 'auto' }}>
                          <code className="text-light" style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                            {snippet.code}
                          </code>
                        </pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-warning">
                Failed to load code snippets. Please try again.
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Event Point</h5>
                <button className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete <strong>{eventType.display_name}</strong>?</p>
                {stats.summary.total_count > 0 && (
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    This event has {formatNumber(stats.summary.total_count)} recorded events.
                    The event type will be deactivated instead of deleted.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={handleDelete} disabled={deleteLoading}>
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default EventPointDetailPage;
